import { useEffect } from 'react'
import { useCallback } from 'react'
import { useState } from 'react'

import { PublicClient } from 'viem'

import { useViemClient } from '@src/hooks/evm/useViemClient'
import { GasFeeMetrics } from '@src/types/gasFees'

export const getEVMGasFeeMetrics = async (
    client: PublicClient,
    gasFeeBufferBips: bigint = BigInt(11000) // 110% in basis points
) => {
    const gasPrice = await client.getGasPrice()
    const chainId = await client.getChainId()

    // Fetch latest block to check EIP-1559 compatibility
    const block = await client.getBlock()
    const baseFeePerGas = block.baseFeePerGas ?? BigInt(0)

    const maxPriorityFeePerGas = await client.estimateMaxPriorityFeePerGas()

    // Add 50% buffer to priority fee
    const suggestedMaxPriorityFeePerGas = (maxPriorityFeePerGas * BigInt(150)) / BigInt(100)

    // Calculate max fee per gas with double the base fee
    const maxFeePerGas = baseFeePerGas
        ? baseFeePerGas * BigInt(2) + suggestedMaxPriorityFeePerGas
        : (gasPrice * BigInt(150)) / BigInt(100)

    const metrics: GasFeeMetrics = {
        chainId: chainId,
        currentGasPrice: (gasPrice * gasFeeBufferBips) / BigInt(10000),
        isEIP1559: !!block.baseFeePerGas,
        EIP1559Params: block.baseFeePerGas
            ? {
                  baseFee: (baseFeePerGas * gasFeeBufferBips) / BigInt(10000),
                  priorityFee: (suggestedMaxPriorityFeePerGas * gasFeeBufferBips) / BigInt(10000),
                  maxFee: (maxFeePerGas * gasFeeBufferBips) / BigInt(10000),
              }
            : undefined,
        isTron: false,
        isSolana: false,
    }

    return metrics
}

export const useGasFeeMetricsEvm = (chainId: number, gasFeeBufferBips: bigint) => {
    const [gasFeeMetrics, setGasMetrics] = useState<GasFeeMetrics | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const client = useViemClient(chainId)

    const fetchEvmGasMetrics = useCallback(async () => {
        setIsLoading(true)
        try {
            if (client) {
                const metrics = await getEVMGasFeeMetrics(client as PublicClient, gasFeeBufferBips)
                setGasMetrics(metrics)
            } else {
                throw new Error(`No client available.`)
            }
        } catch (err) {
            setError(`Error fetching EVM gas metrics: ${(err as Error).message}`)
        } finally {
            setIsLoading(false)
        }
    }, [chainId, client, gasFeeBufferBips])

    useEffect(() => {
        fetchEvmGasMetrics()
    }, [fetchEvmGasMetrics])

    return { gasFeeMetrics, error, isLoading }
}
